import { Text } from '@sitecore-jss/sitecore-jss-react';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import Container from 'components/helpers/Container';
import classnames from 'classnames';
import { TcDirectoryListingProps } from './_types';
import { TcDirectoryListingClasses } from 'components/authorable/NmdpSite/TcDirectoryListing/_classes';

const inlineBlock = classnames('inline-block');

const isValidDateString = (dateString: string): boolean => {
  const date = new Date(dateString);
  return !Number.isNaN(date.getTime()) && date.getFullYear() > 1900;
};

const OverviewSection = ({ fields }: TcDirectoryListingProps): React.JSX.Element => {
  if (!fields?.listing) {
    return <></>;
  }
  const listingField = {
    field: fields.listing.description,
    tag: 'p',
  };
  const memberSinceDate = isValidDateString(fields.listing.memberSince.value)
    ? new Date(fields.listing.memberSince.value).toLocaleString('default', {
        month: 'long',
        year: 'numeric',
      })
    : '';
  const alloTxSinceYear = isValidDateString(fields.listing.allogenicTxSince.value)
    ? new Date(fields.listing.allogenicTxSince.value).getFullYear()
    : '';
  return (
    <div className={TcDirectoryListingClasses.SectionWrapper}>
      <h3 className={TcDirectoryListingClasses.Heading.Medium}>
        <span className={classnames(inlineBlock)}>Overview</span>
      </h3>
      <Text
        field={listingField.field}
        className={classnames('mb-2')}
        editable={false}
        tag={listingField.tag}
        encode
      />
      {fields.listing ? (
        <>
          {alloTxSinceYear && memberSinceDate && (
            <p>
              This center has been performing allogeneic transplants since {alloTxSinceYear} and has
              been an NMDP transplant center since {memberSinceDate}.
            </p>
          )}
          {alloTxSinceYear && !memberSinceDate && (
            <p>
              This center has been performing allogeneic transplants since {alloTxSinceYear}. This
              center is not a member of the NMDP transplant center network.
            </p>
          )}
          {!alloTxSinceYear && memberSinceDate && (
            <p>This center has been an NMDP transplant center since {memberSinceDate}.</p>
          )}
          {!alloTxSinceYear && !memberSinceDate && (
            <p>This center is not a member of the NMDP transplant center network.</p>
          )}
        </>
      ) : (
        <p>
          For detailed information on this center&apos;s transplant program (including program
          description, attending physicians, types of patients treated, cell sources used), please
          contact the center directly.
        </p>
      )}
    </div>
  );
};

const PhysicianSection = ({ fields }: TcDirectoryListingProps): React.JSX.Element => {
  if (!fields?.listing) {
    return <></>;
  }
  return (
    <div className={TcDirectoryListingClasses.SectionWrapper}>
      <h3 className={TcDirectoryListingClasses.Heading.Medium}>
        <span className={classnames(inlineBlock)}>Attending physicians</span>
      </h3>
      {fields.listing.adultPhysicians.value || fields.listing.pedPhysicians.value ? (
        <>
          {fields.listing.adultPhysicians.value &&
            fields.listing.programType?.displayName !== 'Pediatric' && (
              <p className={classnames('mb-2')}>
                <span className={classnames('font-bold')}>Adult</span> -{' '}
                {fields.listing.adultPhysicians.value}
              </p>
            )}
          {fields.listing.pedPhysicians.value &&
            fields.listing.programType?.displayName !== 'Adult' && (
              <p>
                <span className={classnames('font-bold')}>Pediatric</span> -{' '}
                {fields.listing.pedPhysicians.value}
              </p>
            )}
        </>
      ) : (
        <p>This information is currently unavailable.</p>
      )}
    </div>
  );
};

const TransplantsPerformedSection = ({ fields }: TcDirectoryListingProps): React.JSX.Element => {
  if (!fields?.listing) {
    return <></>;
  }
  return (
    <div className={TcDirectoryListingClasses.SectionWrapper}>
      <h3 className={TcDirectoryListingClasses.Heading.Medium}>
        <span className={classnames(inlineBlock)}>Transplants performed</span>
      </h3>
      {fields.listing.txTypes.value ? (
        <>{fields.listing.txTypes.value}</>
      ) : (
        <p>This information is currently unavailable.</p>
      )}
    </div>
  );
};

const CordBloodTransplantsSection = ({ fields }: TcDirectoryListingProps): React.JSX.Element => {
  if (!fields?.listing) {
    return <></>;
  }
  return (
    <div className={TcDirectoryListingClasses.SectionWrapper}>
      <h3 className={TcDirectoryListingClasses.Heading.Medium}>
        <span className={classnames(inlineBlock)}>Cord blood transplants</span>
      </h3>
      {fields.listing.cordBloodDate.value ? (
        <>{fields.listing.cordBloodDate.value}</>
      ) : (
        <p>This information is currently unavailable.</p>
      )}
    </div>
  );
};

const OtherSection = ({ fields }: TcDirectoryListingProps): React.JSX.Element => {
  if (!fields?.listing?.otherServices.value) {
    return <></>;
  }
  return (
    <div className={TcDirectoryListingClasses.SectionWrapper}>
      <h3 className={TcDirectoryListingClasses.Heading.Medium}>
        <span className={classnames(inlineBlock)}>Other programs and services</span>
      </h3>
      <Text field={fields.listing.otherServices} />
    </div>
  );
};

const AboutTab = (allProps: TcDirectoryListingProps): React.JSX.Element => {
  const { fields } = allProps;
  if (!fields?.listing) {
    return <MissingDataSource />;
  }
  return (
    <Container size="standard" dataComponent="authorable/nmdpsite/tcdirectory/about">
      <div>
        <OverviewSection {...allProps} />
        <PhysicianSection {...allProps} />
        <TransplantsPerformedSection {...allProps} />
        <CordBloodTransplantsSection {...allProps} />
        <OtherSection {...allProps} />
      </div>
    </Container>
  );
};

export default AboutTab;
